import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  Notification,
  NotificationCategory,
  NotificationChannel,
  NotificationType,
  PeriodicReport,
  ReportCadence,
} from '@clarity/contract';
import { useScopeContext } from '../../lib/scopeContext.js';
import { useI18n, type Locale } from '../../lib/i18n.js';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationPreferences,
  useNotificationReports,
  useNotifications,
  useUpdateNotificationPreferences,
} from '../../queries/index.js';
import { PushToggle } from './PushToggle.js';

/**
 * The Notifications surface (clarity-smart-notifications Req 9) — the in-app
 * inbox + periodic reports + preferences, reproducing the signed-off mock
 * `brand/notifications-mock.html`. Scope-aware like the other flexible surfaces:
 * a single-branch scope narrows the inbox to that store, an all/brand scope
 * shows everything. Bilingual EN/AR via `useI18n`; the real teal-glass tokens.
 */

type Tab = 'inbox' | 'reports' | 'prefs';
type Filter = 'all' | 'action' | NotificationCategory;

const ACTIONABLE: NotificationType[] = [
  'campaign_finished',
  'campaigns_ready_for_review',
  'new_menu_item',
  'payout_mismatch',
  'store_offline',
  'rating_slipped',
  'ingestion_stalled',
  'balance_threshold',
];

export function Notifications() {
  const { scope, branches, store, isSingleBranch } = useScopeContext();
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('inbox');

  const scopeParam =
    store?.id ?? (isSingleBranch && branches.length === 1 ? branches[0]!.id : 'all');

  return (
    <section className="notif" data-surface="notifications" data-scope={scope.kind}>
      <div className="page-head">
        <div>
          <h2>{t('notif.title')}</h2>
          <p className="sub">{t('notif.sub')}</p>
        </div>
      </div>

      <div className="notif__tabs" role="tablist">
        <TabButton tab="inbox" current={tab} onSelect={setTab} label={t('notif.tab.inbox')} />
        <TabButton tab="reports" current={tab} onSelect={setTab} label={t('notif.tab.reports')} />
        <TabButton tab="prefs" current={tab} onSelect={setTab} label={t('notif.tab.prefs')} />
      </div>

      {tab === 'inbox' && <InboxTab scopeParam={scopeParam} />}
      {tab === 'reports' && <ReportsTab scopeParam={scopeParam} />}
      {tab === 'prefs' && <PreferencesTab />}
    </section>
  );
}

function TabButton({
  tab,
  current,
  onSelect,
  label,
}: {
  tab: Tab;
  current: Tab;
  onSelect: (t: Tab) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={current === tab}
      className="notif__tab"
      onClick={() => onSelect(tab)}
    >
      {label}
    </button>
  );
}

// ── Inbox ────────────────────────────────────────────────────────────────────

const FILTERS: Filter[] = ['all', 'action', 'campaign', 'money', 'menu', 'health'];

function InboxTab({ scopeParam }: { scopeParam: string }) {
  const { t } = useI18n();
  const [filter, setFilter] = useState<Filter>('all');
  const inbox = useNotifications(scopeParam);
  const markAll = useMarkAllNotificationsRead();

  const body = inbox.data?.status === 200 ? inbox.data.body : null;
  const notifications = body?.notifications ?? [];

  const filtered = notifications.filter((n) => {
    if (filter === 'all') return true;
    if (filter === 'action') return Boolean(n.actionPath);
    return n.category === filter;
  });

  const groups = groupByDay(filtered);

  return (
    <>
      <div className="notif__toolbar">
        <div className="notif__filters" role="group">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className="notif__chip"
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
            >
              {t(`notif.filter.${f}` as Parameters<typeof t>[0])}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="ghost"
          onClick={() => markAll.mutate({ body: {} })}
          disabled={(body?.unreadCount ?? 0) === 0}
        >
          {t('notif.markAllRead')}
        </button>
      </div>

      {inbox.isLoading ? (
        <p className="muted" role="status">
          …
        </p>
      ) : filtered.length === 0 ? (
        <EmptyInbox />
      ) : (
        <div className="notif__feed">
          {groups.map(([day, items]) => (
            <div key={day} className="notif__daygroup">
              <h4>{t(`notif.day.${day}` as Parameters<typeof t>[0])}</h4>
              {items.map((n) => (
                <NotificationCard key={n.id} n={n} />
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function EmptyInbox() {
  const { t } = useI18n();
  return (
    <div className="notif__empty">
      <h3>{t('notif.empty.title')}</h3>
      <p className="muted">{t('notif.empty.body')}</p>
    </div>
  );
}

const CHANNEL_LABEL: Record<NotificationChannel, string> = {
  inapp: 'notif.chan.inapp',
  push: 'notif.chan.push',
  whatsapp: 'notif.chan.whatsapp',
};

/** A small category glyph (currentColor) shown in the severity-tinted icon tile. */
function CategoryIcon({ category }: { category: NotificationCategory }) {
  const paths: Record<NotificationCategory, string> = {
    campaign: 'M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4z',
    money: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
    menu: 'M12 5v14M5 12h14',
    health: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
    report: 'M4 19V5m5 14V9m5 10V7m5 12V11',
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[category]} />
    </svg>
  );
}

/**
 * Turn a notification's server-supplied `actionPath` (a scope-less semantic hint
 * like `/stores/{id}/payouts`, `/campaigns`, `/notifications?tab=reports`) into a
 * REAL in-app route under the current client's scope. The server can't know the
 * `/c/:clientId/{all|store/:id}/<surface>` URL shape, so the mapping lives here
 * where the routing context does. Returns `null` when there's nothing to open.
 */
function resolveActionTo(
  actionPath: string | undefined,
  storeIds: string[],
  clientId: string | undefined,
): string | null {
  if (!actionPath || !clientId) return null;
  // Split off the query + hash so they ride through to the target surface
  // (?ref= opens a payout, ?item= opens a catalog item, #availability jumps to
  // a section, ?tab=reports selects a tab).
  const hashSplit = actionPath.split('#');
  const beforeHash = hashSplit[0] ?? '';
  const hash = hashSplit[1] ? `#${hashSplit[1]}` : '';
  const qSplit = beforeHash.split('?');
  const path = qSplit[0] ?? '';
  const query = qSplit[1] ? `?${qSplit[1]}` : '';
  const suffix = `${query}${hash}`;
  const storeId = storeIds[0];

  // `/stores/{id}/<surface...>` → the store-scoped surface or a nested detail
  // route (e.g. `campaigns/{cid}`, `payouts`, `insights`, `catalog`).
  const storeMatch = path.match(/^\/stores\/([^/]+)(?:\/(.+))?$/);
  if (storeMatch) {
    const sid = storeMatch[1] || storeId;
    const rest = storeMatch[2] ?? 'dashboard';
    if (sid) return `/c/${clientId}/store/${sid}/${rest}${suffix}`;
  }

  // Bare surfaces (`/campaigns`, `/notifications`, …) → scoped to the store when
  // we have one, else the all-scope.
  const bare = path.replace(/^\//, '');
  const base = storeId ? `/c/${clientId}/store/${storeId}` : `/c/${clientId}/all`;
  const surface = bare === '' ? 'dashboard' : bare;
  return `${base}/${surface}${suffix}`;
}

function NotificationCard({ n }: { n: Notification }) {
  const { t, locale } = useI18n();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const markRead = useMarkNotificationRead();
  const unread = n.readAt == null;
  const actionTo = resolveActionTo(n.actionPath, n.storeIds, clientId);

  // Opening the action also marks it read (acting on it = seen it).
  const openAction = (): void => {
    if (unread) markRead.mutate({ params: { id: n.id }, body: {} });
    if (actionTo) navigate(actionTo);
  };

  return (
    <article
      className={`card notif-card notif-card--${n.severity}`}
      data-unread={unread}
      data-category={n.category}
    >
      <div className={`notif-card__ico notif-card__ico--${n.severity}`}>
        <CategoryIcon category={n.category} />
      </div>
      <div className="notif-card__body">
        <h3 className="notif-card__title">
          <span>{n.title[locale as Locale] ?? n.title.en}</span>
          <span className="notif-card__tag">{t(`notif.filter.${n.category}` as Parameters<typeof t>[0])}</span>
        </h3>
        <p className="notif-card__txt">{n.body[locale as Locale] ?? n.body.en}</p>
        <div className="notif-card__actions">
          {actionTo && n.actionLabel && (
            <button type="button" className="btn btn--primary" onClick={openAction}>
              {n.actionLabel[locale as Locale] ?? n.actionLabel.en}
            </button>
          )}
          {unread && (
            <button
              type="button"
              className="ghost"
              disabled={markRead.isPending}
              onClick={() => markRead.mutate({ params: { id: n.id }, body: {} })}
            >
              {t('notif.markRead')}
            </button>
          )}
        </div>
      </div>
      <div className="notif-card__side">
        <span className="notif-card__chans">
          {n.channels.map((c) => (
            <span key={c} title={t(CHANNEL_LABEL[c] as Parameters<typeof t>[0])} className="notif-card__chan">
              {t(CHANNEL_LABEL[c] as Parameters<typeof t>[0])}
            </span>
          ))}
        </span>
      </div>
    </article>
  );
}

// ── Reports ──────────────────────────────────────────────────────────────────

function ReportsTab({ scopeParam }: { scopeParam: string }) {
  const { t, locale } = useI18n();
  const reports = useNotificationReports(scopeParam);
  const list = reports.data?.status === 200 ? reports.data.body : [];

  if (reports.isLoading) return <p className="muted" role="status">…</p>;
  if (list.length === 0) return <p className="muted">{t('notif.reports.empty')}</p>;

  return (
    <div className="notif__reports">
      {list.map((r) => (
        <ReportCard key={r.id} report={r.report} locale={locale as Locale} />
      ))}
    </div>
  );
}

function ReportCard({ report, locale }: { report: PeriodicReport; locale: Locale }) {
  const { t } = useI18n();
  return (
    <article className="card notif-report">
      <div className="notif-report__head">
        <span className="notif-report__kicker">{t(`notif.cadence.${report.cadence}` as Parameters<typeof t>[0])}</span>
        <h3>{report.periodLabel[locale] ?? report.periodLabel.en}</h3>
      </div>
      <div className="notif-report__kpis">
        {report.kpis.map((k, i) => (
          <div key={i} className="notif-report__kpi">
            <span className="notif-report__kpi-label">{k.label[locale] ?? k.label.en}</span>
            <span className="notif-report__kpi-val num">
              {k.value}
              {k.unit ? <span className="notif-report__kpi-unit"> {k.unit}</span> : null}
            </span>
            {k.delta && (
              <span className={`notif-report__kpi-delta notif-report__kpi-delta--${k.delta.direction}`}>
                {k.delta.text}
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="notif-report__body">
        {report.sections.map((s) => (
          <div key={s.kind} className="notif-report__section">
            <h4>{s.title[locale] ?? s.title.en}</h4>
            <ul>
              {s.bullets.map((b, i) => (
                <li key={i}>{b[locale] ?? b.en}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </article>
  );
}

// ── Preferences ────────────────────────────────────────────────────────────

const CADENCES: ReportCadence[] = ['3day', 'weekly', 'monthly', 'off'];

function PreferencesTab() {
  const { t } = useI18n();
  const prefsQuery = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();
  const prefs = prefsQuery.data?.status === 200 ? prefsQuery.data.body : null;

  if (prefsQuery.isLoading || !prefs) return <p className="muted" role="status">…</p>;

  const toggleEvent = (type: NotificationType) => {
    const ev = prefs.events[type] ?? { enabled: true, channels: [] };
    update.mutate({ body: { events: { ...prefs.events, [type]: { ...ev, enabled: !ev.enabled } } } });
  };
  const toggleChannel = (type: NotificationType, chan: 'push' | 'whatsapp') => {
    const ev = prefs.events[type] ?? { enabled: true, channels: [] };
    const channels = ev.channels.includes(chan)
      ? ev.channels.filter((c) => c !== chan)
      : [...ev.channels, chan];
    update.mutate({ body: { events: { ...prefs.events, [type]: { ...ev, channels } } } });
  };
  const setCadence = (cadence: ReportCadence) => update.mutate({ body: { reportCadence: cadence } });

  return (
    <div className="notif__prefs">
      <article className="card notif-pref-sec">
        <h3>{t('notif.prefs.eventsTitle')}</h3>
        <p className="muted">{t('notif.prefs.eventsSub')}</p>
        {ACTIONABLE.map((type) => {
          const ev = prefs.events[type] ?? { enabled: true, channels: [] };
          return (
            <div key={type} className="notif-pref-row">
              <button
                type="button"
                role="switch"
                aria-checked={ev.enabled}
                className="notif-switch"
                onClick={() => toggleEvent(type)}
                aria-label={t(`notif.event.${type}` as Parameters<typeof t>[0])}
              />
              <div className="notif-pref-row__main">
                <b>{t(`notif.event.${type}` as Parameters<typeof t>[0])}</b>
              </div>
              <div className="notif-chanpick">
                {(['push', 'whatsapp'] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="notif-chanpick__btn"
                    aria-pressed={ev.channels.includes(c)}
                    disabled={!ev.enabled}
                    onClick={() => toggleChannel(type, c)}
                  >
                    {t(`notif.chan.${c}` as Parameters<typeof t>[0])}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </article>

      <article className="card notif-pref-sec">
        <h3>{t('notif.prefs.reportTitle')}</h3>
        <p className="muted">{t('notif.prefs.reportSub')}</p>
        <div className="notif-pref-row">
          <div className="notif-pref-row__main">
            <b>{t('notif.prefs.cadence')}</b>
          </div>
          <div className="notif-cadence" role="group">
            {CADENCES.map((c) => (
              <button
                key={c}
                type="button"
                className="notif-cadence__btn"
                aria-pressed={prefs.reportCadence === c}
                onClick={() => setCadence(c)}
              >
                {t(`notif.cadence.${c}` as Parameters<typeof t>[0])}
              </button>
            ))}
          </div>
        </div>
      </article>

      <article className="card notif-pref-sec">
        <h3>{t('notif.prefs.channelsTitle')}</h3>
        <p className="muted">{t('notif.prefs.channelsSub')}</p>
        <PushToggle />
      </article>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

type DayBucket = 'today' | 'yesterday' | 'earlier';

function dayBucket(iso: string, now = new Date()): DayBucket {
  const d = new Date(iso);
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return 'today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'yesterday';
  return 'earlier';
}

function groupByDay(items: Notification[]): [DayBucket, Notification[]][] {
  const order: DayBucket[] = ['today', 'yesterday', 'earlier'];
  const map = new Map<DayBucket, Notification[]>();
  for (const n of items) {
    const b = dayBucket(n.createdAt);
    const arr = map.get(b) ?? [];
    arr.push(n);
    map.set(b, arr);
  }
  return order.filter((d) => map.has(d)).map((d) => [d, map.get(d)!]);
}
